import { useEffect, useRef } from "react";

type ConfettiEffectProps = {
  position?: {
    x: number;
    y: number;
  };
};

export function ConfettiEffect({ position }: ConfettiEffectProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Default position is center of screen
    const startX = position?.x || window.innerWidth / 2;
    const startY = position?.y || window.innerHeight / 2;

    // Create confetti particles
    const particles: {
      x: number;
      y: number;
      size: number;
      color: string;
      speed: number;
      angle: number;
      rotation: number;
      rotationSpeed: number;
    }[] = [];

    const colors = ["#374fff", "#e66ca8", "#62c4ba", "#ffffff"];

    // Create particles
    for (let i = 0; i < 50; i++) {
      particles.push({
        x: startX,
        y: startY,
        size: Math.random() * 16 + 8, // Doubled the size
        color: colors[Math.floor(Math.random() * colors.length)],
        speed: Math.random() * 3 + 1,
        angle: Math.random() * Math.PI * 2,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
      });
    }

    // Animation function
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let stillActive = false;

      // Update and draw particles
      particles.forEach((particle) => {
        // Update position
        particle.x += Math.cos(particle.angle) * particle.speed;
        particle.y += Math.sin(particle.angle) * particle.speed + 0.5; // Add gravity

        // Update rotation
        particle.rotation += particle.rotationSpeed;

        // Check if particle is still on screen
        if (
          particle.x > -100 &&
          particle.x < canvas.width + 100 &&
          particle.y > -100 &&
          particle.y < canvas.height + 100
        ) {
          stillActive = true;

          // Draw particle
          ctx.save();
          ctx.translate(particle.x, particle.y);
          ctx.rotate(particle.rotation);

          ctx.fillStyle = particle.color;
          ctx.fillRect(-particle.size / 2, -particle.size / 2, particle.size, particle.size);

          ctx.restore();
        }
      });

      // Continue animation if particles are still active
      if (stillActive) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start animation
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return <canvas ref={canvasRef} style={{
    position: 'fixed', // Changed to fixed to avoid any container scaling issues
    top: 0,
    left: 0,
    width: '100vw', // Using viewport units to ensure proper scaling
    height: '100vh',
    pointerEvents: 'none',
    zIndex: 30,
  }} />;
}
